import { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import store from "../../../store/store";
import { preentitlementrecordApi } from "../../../store/is/preentitlementrecordsapi";
import { buildCertificateUnitRows } from "./certificateUnitRowsUtils";

function parseCertificateDetails(yieldRecord) {
  if (!yieldRecord) return [];
  try {
    const parsed = JSON.parse(yieldRecord);
    if (Array.isArray(parsed)) return parsed;
    if (parsed?.data && Array.isArray(parsed.data)) return parsed.data;
    return [parsed];
  } catch {
    return [];
  }
}

function normalizeBackendRows(data) {
  return data.map((yieldRecord) => ({
    yield_token_id: yieldRecord?.token_id,
    period: yieldRecord?.period,
    asset_id: yieldRecord?.asset_id,
    external_asset_id: yieldRecord?.external_asset_id,
    yield_product_volume: yieldRecord?.product_volume,
    co2_realized: yieldRecord?.co2_realized,
    no_of_entitlement_records: yieldRecord?.no_of_entitlement_records,
    entitlement_record_credits: yieldRecord?.entitlement_record_credits,
    claimed_range_credits: yieldRecord?.claimed_range_credits,
    created: yieldRecord?.created,
  }));
}

export function useCertificateUnitRows({
  isVisible,
  finalTokenUuid,
  certificateType,
  yieldRecord,
}) {
  const [bundleDetailsRows, setBundleDetailsRows] = useState(undefined);
  const [totalBundledCredits, setTotalBundledCredits] = useState(null);
  const [linkageLoading, setLinkageLoading] = useState(false);
  const [linkageError, setLinkageError] = useState(null);

  const certificateDetails = useMemo(
    () => parseCertificateDetails(yieldRecord),
    [yieldRecord]
  );

  useEffect(() => {
    if (!isVisible) {
      setBundleDetailsRows(undefined);
      setTotalBundledCredits(null);
      setLinkageLoading(false);
      setLinkageError(null);
      return;
    }

    if (!finalTokenUuid) {
      setBundleDetailsRows([]);
      setTotalBundledCredits(null);
      setLinkageLoading(false);
      setLinkageError(null);
      return;
    }

    let cancelled = false;
    setLinkageLoading(true);

    (async () => {
      try {
        const result = await store.dispatch(
          preentitlementrecordApi.endpoints.getYieldsByFinalTokenUuid.initiate({
            final_tradable_cert_uuid: finalTokenUuid,
            certificate_type: certificateType,
          })
        );
        if (result.error) {
          throw new Error(
            result.error?.data?.error ||
              result.error?.message ||
              "Failed to load NFRY linkage for certificate"
          );
        }
        const data = result.data?.data;
        if (!Array.isArray(data)) {
          throw new Error("Invalid NFRY linkage response");
        }
        if (!cancelled) {
          setBundleDetailsRows(normalizeBackendRows(data));
          const totalFromFinalTokenId =
            result.data?.total_bundled_credits_by_finaltoken_id;
          const totalFromLinkage = result.data?.total_bundled_credits;
          setTotalBundledCredits(
            totalFromFinalTokenId != null ? totalFromFinalTokenId : totalFromLinkage ?? null
          );
          setLinkageError(null);
        }
      } catch (e) {
        console.error(e);
        const errorMessage = e instanceof Error ? e.message : "Certificate linkage failed";
        message.error(errorMessage);
        if (!cancelled) {
          setBundleDetailsRows([]);
          setTotalBundledCredits(null);
          setLinkageError(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setLinkageLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isVisible, finalTokenUuid, certificateDetails]);

  const certificateUnitRows = useMemo(
    () =>
      buildCertificateUnitRows(certificateDetails[0] || {}, bundleDetailsRows, {
        certificateType,
      }),
    [certificateDetails, bundleDetailsRows, certificateType]
  );

  return {
    certificateDetails,
    certificateUnitRows,
    linkageRows: bundleDetailsRows,
    totalBundledCredits,
    linkageLoading,
    linkageError,
  };
}
